import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, ButtonBase, Card, CircularProgress, Grid, Typography } from "@mui/material";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import classes from "../myteam.module.css";
import { BrudCrumbBackIcon, BrudCrumbNextIcon } from "../../../../components/Icons";
import {
  GEOFENCE_DEFAULT_RADIUS,
  GF,
  clampRadius,
  fetchTeamBySlug,
  formatRadius,
  getErrorMessage,
  hasGeofence,
  placeLabel,
  saveTeamGeofence,
} from "./geofenceApi";
import {
  GeofenceMap,
  MemberChecklist,
  ModeToggle,
  RadiusControl,
  useGeofenceMapsLoader,
} from "./GeofenceParts";

/** Add-geofence flow: step 1 location + radius, step 2 mode + members. */
const GeofenceSetup = () => {
  const navigate = useNavigate();
  const { name: slug } = useParams();
  const { isLoaded } = useGeofenceMapsLoader();

  const [team, setTeam] = useState(null);
  const [step, setStep] = useState(1);
  const [place, setPlace] = useState(null);
  const [radius, setRadius] = useState(GEOFENCE_DEFAULT_RADIUS);
  const [mode, setMode] = useState("time_in_out");
  const [selectedIds, setSelectedIds] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTeamBySlug(slug)
      .then((data) => {
        // Already has a fence: this page only creates, so show the fence instead.
        if (hasGeofence(data)) navigate(`/my-team/${slug}/geofence`, { replace: true });
        else setTeam(data);
      })
      .catch((error) => toast.error(getErrorMessage(error, "Could not load team")));
  }, [slug, navigate]);

  const members = useMemo(
    () => (team?.team_members || []).filter((m) => m && m._id && m.user_name),
    [team]
  );
  // Everyone is selected by default ("applies to everyone in the team").
  const effectiveSelected = useMemo(
    () => selectedIds ?? new Set(members.map((m) => m._id)),
    [selectedIds, members]
  );

  const handleSave = async () => {
    if (!place || !team) return;
    setSaving(true);
    try {
      await saveTeamGeofence(team._id, {
        lat: place.lat,
        lng: place.lng,
        radius_meters: radius,
        name: place.name,
        address: place.address,
        mode,
        excluded_member_ids: members.filter((m) => !effectiveSelected.has(m._id)).map((m) => m._id),
      });
      toast.success("Geofence saved");
      navigate(`/my-team/${slug}/geofence`, { replace: true });
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not save geofence"));
    } finally {
      setSaving(false);
    }
  };

  const label = placeLabel(place) || "Selected location";

  return (
    <div style={{ margin: "0 auto 0 15px" }}>
      <Box sx={{ mb: 3 }} className={classes.brudCrumb}>
        <span onClick={() => (step === 2 ? setStep(1) : navigate(-1))}>
          <BrudCrumbBackIcon />
        </span>
        <span onClick={() => navigate("/my-team")}>My Team</span>
        <BrudCrumbNextIcon />
        <span onClick={() => navigate(`/my-team/${slug}`)}>{team?.team_name || ""}</span>
        <BrudCrumbNextIcon />
        <span style={{ color: "#6A6E8A" }}>Geo Fencing</span>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 600, color: GF.foreground }}>Geo Fencing</Typography>
        <Typography sx={{ fontSize: 13, color: GF.muted }}>
          {step === 1 ? "Step 1 of 2 · Set Location" : "Step 2 of 2 · Select Members"}
        </Typography>
      </Box>

      {!team ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : step === 1 ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ borderRadius: "14px", overflow: "hidden" }}>
              <GeofenceMap isLoaded={isLoaded} place={place} radius={radius} onPlaceChange={setPlace} />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ borderRadius: "14px", p: 3 }}>
              {place ? (
                <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                  <FmdGoodOutlinedIcon sx={{ color: GF.primary, mt: 0.25 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, color: GF.foreground }}>{label}</Typography>
                    {place.address && (
                      <Typography variant="body2" sx={{ color: GF.muted }}>
                        {place.address}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: GF.muted, mb: 3 }}>
                  Search or click on the map to choose a location.
                </Typography>
              )}
              <RadiusControl value={radius} onChange={(v) => setRadius(clampRadius(v))} />
              <Button
                fullWidth
                variant="contained"
                disabled={!place}
                onClick={() => setStep(2)}
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: "14px",
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 600,
                  bgcolor: GF.primary,
                  boxShadow: "none",
                }}
              >
                Next
              </Button>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ maxWidth: 560, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <ButtonBase
            onClick={() => setStep(1)}
            sx={{
              display: "flex",
              gap: 1.25,
              px: 2,
              py: 1.5,
              borderRadius: "12px",
              bgcolor: GF.primaryTint,
              color: GF.primary,
              justifyContent: "flex-start",
            }}
          >
            <FmdGoodOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ flex: 1, textAlign: "left", fontSize: 14, fontWeight: 600 }}>
              {label} · {formatRadius(radius)} radius
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Edit</Typography>
          </ButtonBase>

          <Typography sx={{ fontSize: 14, color: GF.muted }}>
            This geofence applies to everyone in the team by default. Unselect anyone who shouldn’t be
            tracked at this location.
          </Typography>

          <Box sx={{ border: `1px solid ${GF.border}`, borderRadius: "14px", px: "18px", py: 2 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: GF.foreground, mb: 1.5 }}>
              Enable Geofence for
            </Typography>
            <ModeToggle value={mode} onChange={setMode} />
          </Box>

          <MemberChecklist members={members} selectedIds={effectiveSelected} onChange={setSelectedIds} />

          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              py: 1.5,
              borderRadius: "14px",
              textTransform: "none",
              fontSize: 16,
              fontWeight: 600,
              bgcolor: GF.primary,
              boxShadow: "none",
            }}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : "Save Geofence"}
          </Button>
        </Box>
      )}
    </div>
  );
};

export default GeofenceSetup;
